/**
 * Distinguishes a DEFINITIVE auth failure (the session is actually dead —
 * the server said so with a 401, or a codeless 403) from a TRANSIENT one (a
 * 502 during a deploy, a dropped connection, a timeout — the server said
 * nothing about the session at all).
 *
 * Regression (2026-09-03): the founder was bounced to /auth/login roughly
 * daily despite a 30-day-valid Refresh cookie and a live session row. Every
 * failed `POST /api/auth/refresh` in the 24h window was a 502 during a
 * server restart/deploy or a brief upstream stall — never a real
 * unauthorized/forbidden. The client treated ANY refresh failure as
 * "logged out" and nulled the cached user. A 401 is always definitive;
 * everything else (5xx, no response at all, network errors, timeouts) must
 * be retried instead of destroying the session.
 *
 * A 403 is NOT automatically definitive (Codex review on PR #577): it
 * mirrors `shouldRefresh` in axios.ts, which only routes a 403 through
 * refresh when the body carries no structured `code` — every paywall
 * rejection is ALSO a bare 403 with `{ code: "PAYWALL_..." }`. Treating a
 * coded 403 as a dead session would null a paying user's cached identity
 * the moment they hit a paywall. Only a codeless 403 (a genuine
 * ForbiddenException) is definitive.
 */
export const isDefinitiveAuthFailure = (error: unknown): boolean => {
  const response = (error as { response?: { status?: unknown; data?: unknown } } | null | undefined)
    ?.response;
  const status = response?.status;

  if (status === 401) return true;
  if (status !== 403) return false;

  const code = (response?.data as { code?: unknown } | null | undefined)?.code;
  return !code;
};
